<template>
  <div>
    <div class="toolbar-wrapper">
      <cell-tool-bar
        :img-width-percentage="imgWidthPercentage"
        :show-bbox="showBbox"
        :show-normal="showNormal"
        :show-wrong="showWrong"
        :show-excluded="showExcluded"
        :drawing-mode="drawingMode"
        :task-id="currentTaskId"
        :ruler-available="measurementAvailable"
        @img-width-percentage="imgWidthPercentage = $event"
        @show-bbox="showBbox = $event"
        @show-normal="showNormal = $event"
        @show-wrong="showWrong = $event"
        @show-excluded="showExcluded = $event"
        @drawing-mode="drawingMode = $event"
      />
    </div>
    <div class="cell-container">
      <template v-for="type in visibleCellTypes">
        <CellGroupBox
          v-if="groupDict[type] && groupDict[type].length > 0"
          :key="type"
          :cell-type="type"
          :is-z-pressing="isZPressing"
          :show-bbox="showBbox"
          :show-normal="showNormal"
          :show-wrong="showWrong"
          :show-excluded="showExcluded"
          :drawing-mode="drawingMode"
          :img-width="imgWidth"
          :is-read-only="isReadOnly"
          :task-id="currentTaskId"
          :effective-mpp-x="measurementMetadata?.x"
          :effective-mpp-y="measurementMetadata?.y"
          @cell-right-click="handleClick"
          @activate="activate"
        />
      </template>
      <cell-type-menu
        ref="vueSimpleContextMenu"
        :options="cellTypes"
        @option-clicked="optionClicked"
      />
    </div>
    <BModal
      id="lpc-save-conflict-modal"
      :model-value="hasSaveConflict"
      title="Annotations changed"
      no-close-on-backdrop
      no-close-on-esc
      no-header-close
    >
      <p>
        This task was replaced or updated in another session, deleted, or is no longer available
        to you. Reload the task before continuing.
      </p>
      <p class="mb-0">
        Your unsaved changes on this page will be discarded.
      </p>
      <p
        v-if="reloadConflictFailed"
        class="mt-3 mb-0 text-danger"
        role="alert"
      >
        The task could not be reloaded. Check your connection and try again.
      </p>
      <template #footer>
        <BButton
          class="reload-task-button"
          variant="primary"
          :disabled="isReloadingConflict"
          @click="reloadTask"
        >
          {{ isReloadingConflict ? 'Reloading...' : 'Reload task' }}
        </BButton>
      </template>
    </BModal>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount, provide, watch } from 'vue';
import { BButton, BModal, type CheckboxValue } from 'bootstrap-vue-next';

import CellGroupBox from '@/views/components/CellGroupBox.vue';
import CellTypeMenu from '@/views/components/CellTypeMenu.vue';
import { useCells } from '@/composables/useCells';
import { useRepeatTask } from '@/composables/useRepeatTask';
import CellToolBar from '@/views/components/CellToolBar.vue';
import { getDrawOptions, type CellTypeMenuOption, collectNamesFromTree } from '../utils/constants/cellTypeMenuConfig';
import { useConfigStore, useCellGroupStore, useMeasurementRulerStore } from '@/store';
import { useApi, useConstants, useCookie } from '@/composables/useAppGlobals';
import { reportReviewInjectionKey } from '@/views/components/reportReviewContext';
import { isTaskReadOnly } from '@/utils/taskAccess';

const config = useConfigStore();
const cellGroups = useCellGroupStore();
const rulerStore = useMeasurementRulerStore();

const $api = useApi();
const $const = useConstants();
const $cookie = useCookie();

type MenuOrigin = {
  fromType: string;
  fromTileId: number;
  fromImageUrl: string;
};

type CellTypeMenuExpose = {
  showMenu: (event: MouseEvent, origin: MenuOrigin) => void;
  hideContextMenu: () => void;
};

const cellTypes = ref<CellTypeMenuOption[]>([]);
const isZPressing = ref(false);
const imgWidthPercentage = ref('60');
const showBbox = ref<boolean | CheckboxValue | CheckboxValue[] | undefined>(true);
const showNormal = ref<boolean | CheckboxValue | CheckboxValue[] | undefined>(true);
const showWrong = ref<boolean | CheckboxValue | CheckboxValue[] | undefined>(true);
const showExcluded = ref<boolean | CheckboxValue | CheckboxValue[] | undefined>(true);
const drawingMode = ref<boolean | CheckboxValue | CheckboxValue[] | undefined>(false);
const idleTimerId = ref<IntervalHandle | null>(null);
const idleInterval = ref(10000);
const idleCount = ref(0);

const intervalTaskId = ref<IntervalHandle | null>(null);
const repeatInterval = ref(10000);
let cellGroupsRequestToken = 0;
const loadedTaskScope = ref<string | null>(null);
const loadedSessionGeneration = ref<number | null>(null);
const loadedRequestGeneration = ref<number | null>(null);
const snapshotVersion = ref<number | null>(null);
const taskInstanceId = ref<string | null>(null);
const isReloadingConflict = ref(false);
const reloadConflictFailed = ref(false);

const slideInfo = computed(() => config.slideInfo);
const cellGroupID = computed(() => config.cellGroupID);
const isReadOnly = computed(() => config.isReadOnly);
const groupDict = computed(() => cellGroups.groupDict);
const currentTaskId = computed(() => {
  const value = slideInfo.value[$const.SLIDE_KEYS.SLIDE_ID];
  return typeof value === 'string' || typeof value === 'number' ? String(value) : '';
});
const currentTaskScope = computed(() => {
  if (!currentTaskId.value) return null;
  const project = $api.utils.getProjectId() || $api.utils.getProject() || "unscoped";
  return `${project}:${currentTaskId.value}`;
});
const persistenceScope = computed(() => (
  loadedTaskScope.value === currentTaskScope.value
  && loadedSessionGeneration.value === rulerStore.sessionGeneration
  && loadedRequestGeneration.value !== null
  && !isReadOnly.value
    ? `${currentTaskScope.value}:${loadedRequestGeneration.value}`
    : null
));
const measurementMetadata = computed(() =>
  currentTaskId.value ? rulerStore.metadata(currentTaskId.value) : null,
);
const measurementAvailable = computed(() => (
  Boolean(measurementMetadata.value)
  && Boolean(currentTaskId.value)
  && rulerStore.hasRenderableSurface(currentTaskId.value)
));
const reviewContext = useCells(persistenceScope, snapshotVersion, taskInstanceId);
const { hasSaveConflict } = reviewContext;
provide(reportReviewInjectionKey, reviewContext);

const imgWidth = computed(() => $const.DEFAULT_CELL_IMG_SIZE * (Number(imgWidthPercentage.value) / 100));

const staticCellTypes = computed(() => {
  const projectKey = $api.utils.getProject();
  const tree = getDrawOptions({ projectKey, consts: $const });
  return collectNamesFromTree(tree); // ensure the order of static cell types is consistent with the menu
});
const visibleCellTypes = computed(() => {
  const merged = new Set<string>([...staticCellTypes.value, ...Object.keys(groupDict.value || {})]);
  return Array.from(merged);
});

function clearTimer() {
  if (idleTimerId.value != null) {
    window.clearInterval(idleTimerId.value);
    idleTimerId.value = null;
  }
}
function setTimer() {
  if (idleTimerId.value != null) return;
  idleTimerId.value = window.setInterval(handleIdleTimeout, idleInterval.value);
}
function handleIdleTimeout() {
  idleCount.value += 1;
  if (idleCount.value >= 12 && intervalTaskId.value) {
    stopRepeat();
  }
}

function activate() {
  idleCount.value = 0;
  if (!intervalTaskId.value && !isReadOnly.value && persistenceScope.value) {
    startRepeat([triggerSave]);
  }
}

async function getCellGroups(forceReload = false): Promise<boolean> {
  const slideId = currentTaskId.value;
  const taskScope = currentTaskScope.value;
  if (!slideId || !taskScope) return false;
  const requestToken = ++cellGroupsRequestToken;
  const sessionGeneration = rulerStore.sessionGeneration;
  if (
    !forceReload
    && cellGroupID.value === taskScope
    && rulerStore.isMetadataResolved(slideId)
    && typeof snapshotVersion.value === 'number'
    && taskInstanceId.value
  ) {
    loadedTaskScope.value = taskScope;
    loadedSessionGeneration.value = sessionGeneration;
    loadedRequestGeneration.value = requestToken;
    return true;
  }

  stopRepeat();
  loadedTaskScope.value = null;
  loadedSessionGeneration.value = null;
  loadedRequestGeneration.value = null;
  snapshotVersion.value = null;
  taskInstanceId.value = null;
  cellGroups.resetGroups();
  config.setCellGroupID('');
  rulerStore.clearMetadata(slideId);

  try {
    const response = await $api.getCellGroupsResponse(slideId);
    if (
      requestToken !== cellGroupsRequestToken
      || currentTaskScope.value !== taskScope
      || sessionGeneration !== rulerStore.sessionGeneration
      || typeof response.snapshot_version !== 'number'
      || typeof response.task_instance_id !== 'string'
      || !response.task_instance_id
    ) return false;

    cellGroups.setupCellGroups({ groupDict: response.cells, allCellTypes: staticCellTypes.value });
    rulerStore.setMetadata(slideId, response.effective_mpp_x, response.effective_mpp_y);
    if (typeof response.state === 'string') {
      config.updateSlideInfoKey($const.SLIDE_KEYS.STATE, response.state);
    }
    config.updateSlideInfoKey($const.SLIDE_KEYS.ASSIGNEE, response.assignee);
    config.updateSlideInfoKey($const.SLIDE_KEYS.ASSIGNEE_ID, response.assignee_id);
    config.setIsReadOnly(isTaskReadOnly(
      response,
      {
        id: $api.utils.getUserId(),
        name: $cookie.getCookie($const.COOKIES.KEY.USER_NAME),
      },
      $const.TASK_STATES.DONE,
    ));
    if (response.reviewer !== undefined) {
      config.updateSlideInfoKey($const.SLIDE_KEYS.REVIEWER, response.reviewer);
    }
    if (response.last_check_time !== undefined) {
      config.updateSlideInfoKey($const.SLIDE_KEYS.REVIEW_TIME, response.last_check_time);
    }
    config.setCellGroupID(taskScope);
    loadedTaskScope.value = taskScope;
    loadedSessionGeneration.value = sessionGeneration;
    loadedRequestGeneration.value = requestToken;
    snapshotVersion.value = response.snapshot_version;
    taskInstanceId.value = response.task_instance_id;
    reviewContext.clearSaveConflict();
    if (!isReadOnly.value) startRepeat([triggerSave]);
    return true;
  } catch (error) {
    if (requestToken === cellGroupsRequestToken) {
      console.error('Failed to load LPC cell groups', error);
    }
    return false;
  }
}

async function reloadTask() {
  const taskScope = currentTaskScope.value;
  if (!taskScope || isReloadingConflict.value) return;

  reloadConflictFailed.value = false;
  isReloadingConflict.value = true;
  try {
    const loaded = await getCellGroups(true);
    if (!loaded && currentTaskScope.value === taskScope) {
      reloadConflictFailed.value = true;
    }
  } finally {
    isReloadingConflict.value = false;
  }
}

function handleClick(event: MouseEvent, type: string, tileId: number, imageUrl: string) {
  if (isReadOnly.value) return;
  vueSimpleContextMenu.value?.showMenu?.(event, {
    fromType: type,
    fromTileId: tileId,
    fromImageUrl: imageUrl,
  });
  activate();
}

function optionClicked({ item, option }: { item: MenuOrigin; option: CellTypeMenuOption }) {
  if (option.name === $const.BBOX_ACTIONS.CLEAR_BBOX) {
    cellGroups.changeBbox({
      fromType: item.fromType,
      tileId: item.fromTileId,
      imageUrl: item.fromImageUrl,
      bboxes: [],
    });
    return;
  }
  cellGroups.changeCellGroup({
    fromType: item.fromType,
    tileId: item.fromTileId,
    imageUrl: item.fromImageUrl,
    toType: option.name,
  });
}

function onZKeyDown(e: KeyboardEvent) {
  if (e.code === 'KeyZ') {
    isZPressing.value = true;
  }
}
function onZKeyUp(e: KeyboardEvent) {
  if (e.code === 'KeyZ') {
    isZPressing.value = false;
  }
}

const triggerSave = () => { void reviewContext.save(); };
const { startRepeat, stopRepeat } = useRepeatTask({ intervalTaskId, repeatInterval });

const vueSimpleContextMenu = ref<CellTypeMenuExpose | null>(null);

watch(
  currentTaskScope,
  (taskScope) => {
    reloadConflictFailed.value = false;
    cellGroupsRequestToken += 1;
    stopRepeat();
    loadedTaskScope.value = null;
    loadedSessionGeneration.value = null;
    loadedRequestGeneration.value = null;
    snapshotVersion.value = null;
    taskInstanceId.value = null;
    if (!taskScope) {
      cellGroups.resetGroups();
      config.setCellGroupID('');
      return;
    }
    void getCellGroups();
  },
  { immediate: true },
);

watch(hasSaveConflict, (hasConflict) => {
  if (!hasConflict) return;
  stopRepeat();
  loadedTaskScope.value = null;
  loadedSessionGeneration.value = null;
  loadedRequestGeneration.value = null;
  snapshotVersion.value = null;
  taskInstanceId.value = null;
});

watch(
  () => rulerStore.sessionGeneration,
  () => {
    reloadConflictFailed.value = false;
    cellGroupsRequestToken += 1;
    stopRepeat();
    loadedTaskScope.value = null;
    loadedSessionGeneration.value = null;
    loadedRequestGeneration.value = null;
    snapshotVersion.value = null;
    taskInstanceId.value = null;
    cellGroups.resetGroups();
    config.setCellGroupID('');
  },
);

onMounted(() => {
  const drawOptions: CellTypeMenuOption[] = [
    { name: 'bbox action', type: 'divider' },
    ...Object.values($const.BBOX_ACTIONS).map<CellTypeMenuOption>((action) => ({ name: action })),
    { name: 'to type', type: 'divider' },
  ];
  const projectKey = $api.utils.getProject();
  const tree = getDrawOptions({ isRbc: false, projectKey, consts: $const });
  cellTypes.value = drawOptions.concat(tree);
  if (!isReadOnly.value) {
    repeatInterval.value = 30000;
    if (persistenceScope.value) startRepeat([triggerSave]);
    setTimer();
  }
  document.body.addEventListener('keydown', onZKeyDown);
  document.body.addEventListener('keyup', onZKeyUp);
});

onBeforeUnmount(() => {
  cellGroupsRequestToken += 1;
  loadedTaskScope.value = null;
  loadedSessionGeneration.value = null;
  loadedRequestGeneration.value = null;
  snapshotVersion.value = null;
  taskInstanceId.value = null;
  document.body.removeEventListener('keydown', onZKeyDown);
  document.body.removeEventListener('keyup', onZKeyUp);
  clearTimer();
  stopRepeat();
});
</script>
<style lang="scss" scoped>
.cell-container {
  background-color: white;
  margin-top: 12%;
}

.toolbar-wrapper {
  position: relative;
}

.reload-task-button {
  min-width: 8rem;
}
</style>
